<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useDataInventoryStore } from '../../stores/dataInventoryStore';
import { formatDate } from '../../utils/dateFormatting';
import type { DataTypeAvailability } from '../../stores/dataInventoryStore';

const { t, n } = useI18n();
const dataInventoryStore = useDataInventoryStore();

// The date line is shown only when the type has a full, derivable range —
// both endpoints present (see the "omit the date entirely" decision). Types
// with no creation column report a count and nothing else.
const dateRange = (type: DataTypeAvailability): string | null => {
    if (!type.earliest_date || !type.latest_date) return null;
    return t('common.dates.range', {
        start: formatDate(type.earliest_date),
        end: formatDate(type.latest_date),
    });
};
</script>

<template>
    <aside class="data-inventory-panel" :aria-label="t('dataInventory.title')">
        <div class="data-inventory-header">
            <h2 class="data-inventory-title">{{ t('dataInventory.title') }}</h2>
            <button
                type="button"
                class="data-inventory-close"
                :title="t('common.close')"
                :aria-label="t('common.close')"
                @click="dataInventoryStore.close()"
            >
                <span class="material-icons">close</span>
            </button>
        </div>

        <div class="data-inventory-body">
            <div v-if="dataInventoryStore.loading" class="data-inventory-status">
                {{ t('common.loading') }}
            </div>
            <div v-else-if="dataInventoryStore.error" class="data-inventory-status">
                {{ t('dataInventory.error') }}
            </div>
            <div
                v-else-if="dataInventoryStore.dataTypes.length === 0"
                class="data-inventory-status"
            >
                {{ t('dataInventory.empty') }}
            </div>

            <div v-else class="data-inventory-cards">
                <div
                    v-for="type in dataInventoryStore.dataTypes"
                    :key="type.type_name"
                    class="data-inventory-card"
                >
                    <h3 class="data-inventory-card-name">{{ type.display_name }}</h3>
                    <p
                        v-if="type.short_display_description"
                        class="data-inventory-card-description"
                    >
                        {{ type.short_display_description }}
                    </p>
                    <div class="data-inventory-card-meta">
                        <span class="data-inventory-card-count">
                            {{
                                t(
                                    'dataInventory.records',
                                    { formatted: n(type.record_count, 'integer') },
                                    type.record_count,
                                )
                            }}
                        </span>
                        <template v-if="dateRange(type)">
                            <span class="data-inventory-card-sep">·</span>
                            <span class="data-inventory-card-range">{{ dateRange(type) }}</span>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </aside>
</template>
